import * as React from 'react'
import { Theme } from '@mui/material/styles'
import { Field, useFormikContext } from 'formik'
import { ILeadFormModel } from '../typings'
import {
  Assignment,
  FollowUpInformation,
  LocationInformation,
  MarketingAttribution,
} from '../FormComponents'
import { useCreateOppActions } from '@user/components/Location'
import Grid from '@mui/material/Grid'
import { LeadCustomFields } from '@user/components/Leads/AddLeadForm/FormSections/LeadCustomFields'
import { useScrollToFieldError } from '@user/components/Formik/ScrollToFieldError/useScrollToFieldError'
import { ScrollToErrorMarker } from '@user/components/Formik/ScrollToErrorMarker'
import { DraftLead } from '@user/store/draftLeads'
import { useData, useEffects, useHandlers } from './hooks'
import { ISource } from '@user/store/sources'
import { ICampaign } from '@user/store/campaigns'
import Paper from '@mui/material/Paper'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'
import { MuiCheckboxFormik } from '@user/components/Formik/MuiCheckboxFormik'
import {
  addLeadLocationSection,
  addLeadSectionLabels,
} from '@user/components/Settings/EntityFormSettings/constants'
import { TargetPestsSection } from '../FormComponents/TargetPestsSection/TargetPestsSection'
import { TextFieldProps } from '@mui/material/TextField'
import Stack from '@mui/material/Stack'

export interface AddLeadProps {
  hideOptional: boolean
  innerRef: React.RefObject<HTMLDivElement>
  disabled?: boolean
  isMappedWithUpdateLocationPerm?: boolean
  isDraftLead: boolean
  addFromLocation?: boolean | '' | null
  createOppActions: ReturnType<typeof useCreateOppActions>
  draftLead?: DraftLead
  activeLeadSources: ISource[]
  activeLeadCampaigns: ICampaign[]
  spacing?: number
  textFieldProps?: TextFieldProps
  noPaper?: boolean
  fromMapView?: boolean
}

export function MainForm(props: AddLeadProps) {
  const {
    activeLeadSources,
    activeLeadCampaigns,
    disabled,
    isDraftLead,
    addFromLocation,
    draftLead,
    hideOptional,
    spacing = 2,
    textFieldProps,
    noPaper = false,
    fromMapView = false,
  } = props

  const { values, errors, submitCount } = useFormikContext<ILeadFormModel>()

  const { branchId, referral, salesTeamId, externalLocationId } = values

  const {
    activeAttributionSection,
    showFollowUpSection,
    campaigns,
    defaultFunnel,
    defaultSalesTeam,
    divisions,
    entitySettingsMap,
    firstStage,
    hasEnabledCustomFields,
    hasRequiredCustomFields,
    leadLocationInfo,
    leadSources,
    loadSalesTeamUsers,
    locationTypes,
    nameRequirementMode,
    salesFunnels,
    salesFunnelStages,
    salesTeams,
    showOtherSection,
  } = useData({ hideOptional })

  const { onChangeReferral, onChangeSource, onChangeCampaign } = useHandlers({
    activeLeadSources,
    activeLeadCampaigns,
  })

  useEffects({
    campaigns,
    leadSources,
    loadSalesTeamUsers,
    defaultSalesTeam,
    defaultFunnel,
    firstStage,
    isDraftLead,
    draftLead,
    locationTypes,
    divisions,
  })

  useScrollToFieldError()

  const content = (
    <Grid container>
      {/* ========== F O R M ========== */}

      <Grid container item mb={fromMapView && !externalLocationId ? 2 : 4}>
        <Typography variant="h6" mb={2}>
          {addLeadSectionLabels[addLeadLocationSection]}
        </Typography>
        {errors['nameGroup'] && submitCount > 0 && (
          <>
            <ScrollToErrorMarker fieldName="nameGroup" />
            <Typography
              variant="body2"
              color="error"
              width="100%"
              mb={2}
              mt={-1.5}
            >
              {errors['nameGroup']}
            </Typography>
          </>
        )}

        <LocationInformation
          locationTypes={locationTypes}
          disabled={disabled}
          leadLocationInfo={leadLocationInfo}
          divisions={divisions}
          hideOptional={hideOptional}
          settingsMap={entitySettingsMap}
          spacing={spacing}
          textFieldProps={textFieldProps}
          fromMapView={fromMapView}
          nameRequirementMode={nameRequirementMode}
        />
      </Grid>

      {!externalLocationId && (
        <Stack mb={fromMapView ? 0 : 4} width="100%">
          <Typography variant="h6" mb={fromMapView ? 0.8 : 2}>
            Bill to
          </Typography>
          <Grid container item>
            <Field
              component={MuiCheckboxFormik}
              label="Same as Location Information"
              name="locationSameAsBillTo"
              size="small"
              color="primary"
              margin={0}
              data-test-id="same-as-location-information-checkbox"
            />
          </Grid>

          {errors['billTo_nameGroup'] && submitCount > 0 && (
            <>
              <ScrollToErrorMarker fieldName="billTo_nameGroup" />
              <Typography variant="body2" color="error" width="100%" mt={1}>
                {errors['billTo_nameGroup']}
              </Typography>
            </>
          )}

          <Grid
            container
            item
            mt={fromMapView ? 1.8 : !values.locationSameAsBillTo ? 2 : 0}
          >
            <Collapse in={!values.locationSameAsBillTo} unmountOnExit>
              <LocationInformation
                locationTypes={locationTypes}
                disabled={disabled}
                leadLocationInfo={leadLocationInfo}
                divisions={divisions}
                hideOptional={hideOptional}
                settingsMap={entitySettingsMap}
                spacing={spacing}
                fieldNamePrefix="billTo_"
                textFieldProps={textFieldProps}
                externalLocationId={externalLocationId}
                nameRequirementMode={nameRequirementMode}
              />
            </Collapse>
          </Grid>
        </Stack>
      )}

      {showFollowUpSection && (
        <FollowUpInformation
          hideOptional={hideOptional}
          settingsMap={entitySettingsMap}
          spacing={spacing}
          textFieldProps={textFieldProps}
        />
      )}

      <TargetPestsSection
        settingsMap={entitySettingsMap}
        spacing={spacing}
        textFieldProps={textFieldProps}
      />

      <Assignment
        salesTeams={salesTeams}
        salesFunnels={salesFunnels}
        salesFunnelStages={salesFunnelStages}
        branchId={branchId}
        addFromLocation={addFromLocation}
        spacing={spacing}
        hideOptional={hideOptional}
        textFieldProps={textFieldProps}
      />

      {activeAttributionSection && (
        <MarketingAttribution
          leadSources={activeLeadSources}
          campaigns={activeLeadCampaigns}
          onChangeCampaign={onChangeCampaign}
          onChangeReferral={onChangeReferral}
          onChangeSource={onChangeSource}
          referral={referral}
          salesTeamId={salesTeamId}
          settingsMap={entitySettingsMap}
          spacing={spacing}
          textFieldProps={textFieldProps}
        />
      )}

      <LeadCustomFields
        customFieldsVisible={hasEnabledCustomFields || hasRequiredCustomFields}
        hideOptional={hideOptional}
        show={showOtherSection}
        spacing={spacing}
        textFieldProps={textFieldProps}
        avoidValidateOnBlur
      />
    </Grid>
  )

  return noPaper ? (
    content
  ) : (
    <Paper
      elevation={0}
      sx={{
        padding: (theme: Theme) => theme.spacing(2),
        maxWidth: '660px',
        marginBottom: (theme: Theme) => theme.spacing(2),
        border: 'solid 1px #e0e0e0',
      }}
    >
      {content}
    </Paper>
  )
}
